/**
 * useRolePermissions — React hook that returns a can() helper
 * for synchronous, role-based access checks in UI components (nav, field gating, etc.)
 *
 * Uses the current identity's role + the configuration store's rolePermissions.
 * Mirrors the logic in canAccess.ts but is available as a React hook.
 */
import { useGetIdentity } from "ra-core";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { defaultRolePermissions } from "../../root/defaultConfiguration";
import type { Permission } from "../../types";

export const useRolePermissions = () => {
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();

  const role = (identity as any)?.role ?? "sales";
  const permissions = config.rolePermissions ?? defaultRolePermissions;

  /**
   * @param resource  The resource/module key (e.g. "orders", "ar", "factory_cost")
   * @param action    "read" (list/show) | "write" (create/edit/delete)
   */
  const can = (resource: string, action: "read" | "write" = "read"): boolean => {
    if (role === "admin") return true;

    const resourcePerms = permissions[resource];
    if (!resourcePerms) return false;

    const perm: Permission = (resourcePerms[role] as Permission) ?? "none";
    if (perm === "none") return false;
    if (action === "read") return true; // read/own/all all allow viewing
    return perm === "all" || perm === "own"; // write requires own or all
  };

  return { can, role };
};
